export type StandingRow = {
  team: string;
  flag: string;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  diff: number;
  points: number;
};

export type Game = {
  id: string;
  game_no: number;
  group_label: string;
  phase: string;
  home_team: string;
  away_team: string;
  home_flag: string;
  away_flag: string;
  home_score: number | null;
  away_score: number | null;
  is_finished: boolean;
  kickoff_time: string;
  // Live / status extras — present on real games (normalizeGameToSnake maps
  // them); absent on synthetic placeholder bracket entries. Optional so the
  // placeholder cast and computeStandings (which ignores them) stay valid.
  live_home_score?: number | null;
  live_away_score?: number | null;
  is_live?: boolean;
  status_short?: string | null;
};

export function computeStandings(games: Game[]): Record<string, StandingRow[]> {
  const groupGames = games.filter((g) => g.phase === "group");
  const byGroup: Record<string, Game[]> = {};
  for (const g of groupGames) {
    (byGroup[g.group_label] = byGroup[g.group_label] ?? []).push(g);
  }

  const result: Record<string, StandingRow[]> = {};
  for (const [label, list] of Object.entries(byGroup)) {
    const teams = new Map<string, StandingRow>();
    const ensure = (team: string, flag: string) => {
      if (!teams.has(team)) {
        teams.set(team, {
          team, flag, played: 0, wins: 0, draws: 0, losses: 0,
          goalsFor: 0, goalsAgainst: 0, diff: 0, points: 0,
        });
      }
      return teams.get(team)!;
    };

    for (const g of list) {
      const h = ensure(g.home_team, g.home_flag);
      const a = ensure(g.away_team, g.away_flag);
      if (!g.is_finished || g.home_score == null || g.away_score == null) continue;
      h.played++; a.played++;
      h.goalsFor += g.home_score; h.goalsAgainst += g.away_score;
      a.goalsFor += g.away_score; a.goalsAgainst += g.home_score;
      if (g.home_score > g.away_score) { h.wins++; h.points += 3; a.losses++; }
      else if (g.home_score < g.away_score) { a.wins++; a.points += 3; h.losses++; }
      else { h.draws++; a.draws++; h.points++; a.points++; }
    }

    const rows = Array.from(teams.values()).map((r) => ({ ...r, diff: r.goalsFor - r.goalsAgainst }));
    rows.sort((x, y) =>
      y.points - x.points ||
      y.diff - x.diff ||
      y.goalsFor - x.goalsFor ||
      x.team.localeCompare(y.team)
    );
    result[label] = rows;
  }
  return result;
}

export function groupProgress(games: Game[], label: string): { played: number; total: number } {
  const list = games.filter((g) => g.phase === "group" && g.group_label === label);
  return { played: list.filter((g) => g.is_finished).length, total: list.length };
}
